import { CommonNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { useNodeViewCollaborationPresence } from '@/hooks/use-node-view-collaboration-presence';
import type { OnMount } from '@monaco-editor/react';
import { Editor as MonacoEditor } from '@monaco-editor/react';
import { NodeViewContent, type ReactNodeViewProps } from '@tiptap/react';
import { memo, useState } from 'react';
import { setupMonacoEditorOnMount } from '../monaco-editor-utils';

const ZyConsoleNodeView: React.FC<ReactNodeViewProps> = ({ node, updateAttributes, selected, editor, getPos, decorations }) => {
    const isEditorReadonly = useIsEditorReadonly(editor);
    const preserveWhiteSpace = node.attrs['preserve-white-space'] || false;
    const highlight = node.attrs.highlight || '';
    const { id } = node.attrs;
    const consoleContent = node.textContent || '';

    const [isMonacoEditorFocused, setIsMonacoEditorFocused] = useState(false);
    const { presenceUsers, presenceTracking } = useNodeViewCollaborationPresence(editor, id, 'zyConsole', isMonacoEditorFocused);

    const editorOptions = {
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        automaticLayout: true,
    };

    const handleContentChange = (value: string | undefined) => {
        if (value === undefined) return;

        const pos = getPos();

        if (typeof pos === 'number') {
            const { state } = editor;
            const currentNode = state.doc.nodeAt(pos);
            if (currentNode) {
                const start = pos + 1;
                const end = pos + currentNode.nodeSize - 1;
                const tr = state.tr;
                if (value.length > 0) {
                    tr.replaceWith(start, end, state.schema.text(value));
                } else {
                    tr.delete(start, end);
                }
                editor.view.dispatch(tr);
            }
        }
    };

    const handlePreserveWhiteSpaceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        updateAttributes({ 'preserve-white-space': e.target.value === 'true' });
    };

    const handleHighlightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateAttributes({ highlight: e.target.value || null });
    };

    const customSettingsPanel = (
        <div
            contentEditable={false}
            className="rounded border-b border-gray-200 bg-gray-50 px-4 pt-6 pb-3 dark:border-gray-700 dark:bg-gray-800"
        >
            <fieldset
                disabled={isEditorReadonly}
                className="m-0 flex items-start gap-4 border-0 p-0"
            >
                <div className="flex-1">
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Preserve White Space</label>
                    <select
                        value={preserveWhiteSpace ? 'true' : 'false'}
                        onChange={handlePreserveWhiteSpaceChange}
                        className="w-full rounded border border-gray-300 bg-white px-3 py-1 text-sm text-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                    >
                        <option value="false">False</option>
                        <option value="true">True</option>
                    </select>
                </div>
                <div className="flex-1">
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Highlight Lines</label>
                    <input
                        type="text"
                        value={highlight}
                        onChange={handleHighlightChange}
                        placeholder="1,4,5"
                        className="w-full rounded border border-gray-300 bg-white px-3 py-1 text-sm text-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                    />
                </div>
            </fieldset>
        </div>
    );

    const handleMonacoMount: OnMount = instance => {
        setupMonacoEditorOnMount(
            instance,
            () => setIsMonacoEditorFocused(true),
            () => setIsMonacoEditorFocused(false),
        );
    };

    const monacoEditor = (
        <MonacoEditor
            language="plaintext"
            value={consoleContent}
            onChange={handleContentChange}
            options={{ ...editorOptions, readOnly: isEditorReadonly }}
            onMount={handleMonacoMount}
        />
    );

    return (
        <CommonNodeViewWrapper
            decorations={decorations}
            label="zyConsole"
            selected={selected}
            settingsPanel={customSettingsPanel}
            contentClassName="p-0"
            containerClassName="my-6 rounded border dark:bg-gray-900"
            presenceTracking={presenceTracking}
            presenceUsers={presenceUsers}
            fullscreenContent={monacoEditor}
        >
            <NodeViewContent className="hidden" />
            <div
                className="h-40 w-full min-w-0 overflow-hidden"
                contentEditable={false}
            >
                {monacoEditor}
            </div>
        </CommonNodeViewWrapper>
    );
};

export default memo(ZyConsoleNodeView);
